import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { talentLenzOpportunityAccessApi } from "../../talentLenz/talentLenzOpportunityAccessApi";
import { platformAccessApi } from "../api/platformAccessApi";
import { useAccess } from "./AccessProvider";
import AccessRouteState from "./AccessRouteState";
import { ProductAccessProvider } from "./ProductAccessContext";
import { accessSnapshotKey } from "./accessCachePolicy";

function accessFailureReason(error) {
  const code = String(error?.code || error?.details?.code || "");
  if (code === "functions/permission-denied") return "product";
  if (code === "functions/unauthenticated") return "session";
  return "service";
}

export default function ProductRoute({ productKey, moduleKey, children }) {
  const location = useLocation();
  const { user, activeTenant, loading, error, refreshAccessContext } = useAccess();
  // Membership.updatedAt alone does not cover company allocation, module or
  // branding changes. Include the effective tenant and account in validation.
  const validationScope = accessSnapshotKey([
    user?.uid, user?.emailVerified, activeTenant, productKey, moduleKey || "",
  ]);
  const [validation, setValidation] = useState({
    scope: null,
    loading: true,
    allowed: false,
    context: null,
    failureReason: null,
  });

  const allocated = useMemo(() => {
    const product = activeTenant?.systems?.find((system) => system.key === productKey);
    if (!product) {
      // The Tenant Admin system card can be fresher than the central access
      // snapshot. Let the authoritative launch callable decide Pre-Audit
      // management access instead of rejecting an allocated admin locally.
      return productKey === "preAudit" &&
        activeTenant?.tenantRole === "tenant_admin" &&
        Boolean(activeTenant?.tenantId);
    }
    if (!moduleKey) return true;
    return product.modules?.[moduleKey] === true;
  }, [activeTenant, productKey, moduleKey]);

  useEffect(() => {
    let active = true;
    if (loading || !user || !allocated) {
      setValidation({
        scope: validationScope,
        // A restored tenant may arrive before central initialization completes.
        // Keep this scope pending so the next render cannot redirect before the
        // effect starts its product verification.
        loading: Boolean(loading),
        allowed: false,
        context: null,
        failureReason: null,
      });
      return () => {
        active = false;
      };
    }

    setValidation({
      scope: validationScope,
      loading: true,
      allowed: false,
      context: null,
      failureReason: null,
    });
    const contextRequest = user.emailVerified === false &&
      productKey === "talentLenz" ?
      talentLenzOpportunityAccessApi.getProductLaunchContext() :
      platformAccessApi.getProductLaunchContext(productKey, moduleKey);
    contextRequest
      .then((context) => {
        if (active) {
          setValidation({
            scope: validationScope,
            loading: false,
            allowed: true,
            context,
            failureReason: null,
          });
        }
      })
      .catch(async (nextError) => {
        if (active && refreshAccessContext &&
            ["functions/permission-denied", "functions/unauthenticated"].includes(nextError?.code)) {
          try {
            await refreshAccessContext({ forceTokenRefresh: true, background: true });
          } catch {
            // One access revalidation, with no replay of user mutations.
          }
        }
        if (active) {
          setValidation({
            scope: validationScope,
            loading: false,
            allowed: false,
            context: null,
            failureReason: accessFailureReason(nextError),
          });
        }
      });
    return () => {
      active = false;
    };
  }, [loading, user, allocated, productKey, moduleKey, validationScope, refreshAccessContext]);

  if (loading || validation.scope !== validationScope || validation.loading) return <AccessRouteState title="Verifying system access..." />;
  if (!user) return <Navigate to="/login" replace state={{ from:
    /^\/unity\/learner\/attendance\//.test(location.pathname)
      ? location.pathname + location.search + location.hash : location.pathname }} />;
  if (error) {
    return <Navigate to={`/access-pending?reason=${accessFailureReason(error)}`} replace />;
  }
  if (!allocated) return <Navigate to="/access-pending?reason=product" replace />;
  if (!validation.allowed) {
    return (
      <Navigate
        to={`/access-pending?reason=${validation.failureReason || "product"}`}
        replace
      />
    );
  }
  return (
    <ProductAccessProvider value={validation.context}>
      {children}
    </ProductAccessProvider>
  );
}
